/**
 * What you can do about an AI agent writing the file you are in (collaboration phase 4, the design's "Person and AI
 * agent"): "Tell the agent" (a note for that agent only, about your line: it comes with its next tool result, so it
 * re-reads instead of overwriting; vfs-service `POST /collab/notes`) and "Notify me when it's done" (a notification
 * when its run ends; `POST /collab/agents/:id/watch`). Never a lock: you keep typing.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { addNote, watchAgent } from '../../services/CollabService';
import { linesLabel } from '../../utils/presenceModel';
import './Collab.css';

/** The note's first words: what you are doing where, and what you ask. */
export function tellDraft({ path, line, ranges }) {
  const file = String(path || '').split('/').pop() || 'this file';
  const where = Number.isInteger(line) ? `line ${line}` : ranges?.length ? linesLabel(ranges) : 'it';
  return `I am editing ${file} around ${where}. Re-read the file before your next write and keep my changes there.`;
}

export default function AgentActions({ projectId, agent, path, line = null, ranges = [] }) {
  const [telling, setTelling] = useState(false);
  const [draft, setDraft] = useState('');
  const [told, setTold] = useState(false);
  const [watching, setWatching] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!projectId || !agent?.id) { return null; }

  const openTell = () => {
    setDraft(tellDraft({ path, line, ranges }));
    setTelling(true);
  };
  const send = async () => {
    if (!draft.trim()) { return; }
    setBusy(true);
    try {
      await addNote(projectId, { to: 'agents', agent: agent.id, path, line: Number.isInteger(line) ? line : undefined, text: draft.trim() });
      setTold(true);
      setTelling(false);
      window.dispatchEvent(new CustomEvent('collab:note', { detail: { projectId, path } }));
    } catch (error) {
      message.error(error.message || 'The note could not be left.');
    } finally {
      setBusy(false);
    }
  };
  const toggleWatch = async () => {
    setBusy(true);
    try {
      const answer = await watchAgent(projectId, agent.id, !watching);
      setWatching(Boolean(answer?.watching));
      if (answer?.watching) { message.success('You will get a notification when it is done.'); }
    } catch (error) {
      message.error(error.message || 'Could not do that now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {told ? <span className="collab-muted collab-small" data-testid="agent-told">Told: it reads your note with its next step</span> : (
        <button className="collab-link" data-testid="tell-agent" disabled={busy} onClick={openTell} type="button">Tell the agent</button>
      )}
      <button aria-pressed={watching} className="collab-link" data-testid="watch-agent" disabled={busy} onClick={toggleWatch} type="button">
        {watching ? 'You will be notified · undo' : 'Notify me when it’s done'}
      </button>
      {telling && (
        <span className="collab-tell" data-testid="tell-agent-form">
          <textarea aria-label="Your note for the agent" onChange={(event) => setDraft(event.target.value)} value={draft} />
          <button className="collab-btn is-primary" disabled={busy || !draft.trim()} onClick={send} type="button">Send</button>
          <button className="collab-btn" disabled={busy} onClick={() => setTelling(false)} type="button">Cancel</button>
        </span>
      )}
    </>
  );
}

AgentActions.propTypes = {
  agent: PropTypes.object,
  line: PropTypes.number,
  path: PropTypes.string,
  projectId: PropTypes.string,
  ranges: PropTypes.arrayOf(PropTypes.array),
};
